<template>
  <div>
    <v-card-title class="body-2 v-card__subtitle">
      <v-icon small class="mr-2">mdi-chart-bar</v-icon>
      Erfüllungsrate je Zielvorgabe
      <v-spacer></v-spacer>
      <v-chip small outlined text>
        <v-icon small left>mdi-cursor-pointer</v-icon>
        Klicken für Details
      </v-chip>
    </v-card-title>
    <v-card-text v-if="!loading && hasData">
      <VueApexCharts
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
        :height="height"
      ></VueApexCharts>
    </v-card-text>
    <v-card-text
      v-else-if="loading"
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-card-text>
    <v-card-text
      v-else
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <div class="text-center grey--text">
        <v-icon large color="grey">mdi-chart-bar</v-icon>
        <div class="mt-2">Keine Daten verfügbar</div>
      </div>
    </v-card-text>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import apexChartsDarkMode from "@/mixins/apexChartsDarkMode";

export default {
  name: "TargetsCompletionChart",
  mixins: [apexChartsDarkMode],
  components: {
    VueApexCharts,
  },
  props: {
    targets: {
      type: Array,
      required: true,
    },
    performanceData: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 300,
    },
  },
  computed: {
    hasData() {
      return this.targets.length > 0 && this.performanceData.length > 0;
    },
    chartSeries() {
      if (!this.hasData) return [];

      const metData = [];
      const notMetData = [];

      this.targets.forEach((target) => {
        const metCount = this.performanceData.filter((member) => {
          const targetResult = member.performanceTargetResults[target.meta.id];
          return targetResult && targetResult.status === "met";
        }).length;

        const notMetCount = this.performanceData.filter((member) => {
          const targetResult = member.performanceTargetResults[target.meta.id];
          return targetResult && targetResult.status === "notMet";
        }).length;

        metData.push(metCount);
        notMetData.push(notMetCount);
      });

      return [
        {
          name: "Erfüllt",
          data: metData,
        },
        {
          name: "Nicht erfüllt",
          data: notMetData,
        },
      ];
    },
    chartOptions() {
      // const maxValue = Math.max(...this.chartSeries.flatMap((s) => s.data), 10);
      const vm = this;

      return {
        ...this.apexChartsDarkModeOptions,
        chart: {
          ...this.apexChartsDarkModeOptions.chart,
          type: "bar",
          toolbar: {
            show: true,
          },
          stacked: true,
          events: {
            dataPointSelection: function (event, chartContext, config) {
              vm.handleBarClick(config);
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              position: "center",
            },
          },
        },
        colors: ["#4CAF50", "#F44336"],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val > 0 ? val : "";
          },
          style: {
            fontSize: "12px",
            colors: ["#fff"],
            fontWeight: "bold",
          },
        },
        xaxis: {
          categories: this.targets.map((t) => t.shortTitle || t.title),
          title: {
            text: "Anzahl Mitglieder",
          },
        },
        yaxis: {
          labels: {
            maxWidth: 120,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
        tooltip: {
          y: {
            formatter: function (val, { dataPointIndex, w }) {
              const series = w.config.series;
              const total = series.reduce(
                (sum, s) => sum + s.data[dataPointIndex],
                0
              );
              const percentage =
                total > 0 ? ((val / total) * 100).toFixed(1) : 0;
              return val + " (" + percentage + "%)";
            },
          },
        },
      };
    },
  },
  methods: {
    handleBarClick(config) {
      const { seriesIndex, dataPointIndex } = config;
      const target = this.targets[dataPointIndex];
      const status = seriesIndex === 0 ? "met" : "notMet";

      // Get members for this target and status
      const filteredMembers = this.performanceData
        .filter((member) => {
          const targetResult = member.performanceTargetResults[target.meta.id];
          return targetResult && targetResult.status === status;
        })
        .map((member) => {
          const targetResult = member.performanceTargetResults[target.meta.id];
          const unit = target.unit || "";
          return {
            member: member,
            user: member.user,
            group: member.group,
            summary: `Ist-Wert: ${
              targetResult.value !== undefined ? targetResult.value : "N/A"
            }${unit ? " " + unit : ""}`,
          };
        });

      // Group members by user group and sort
      const groupedMembers = {};
      filteredMembers.forEach((item) => {
        const groupKey = item.group.title;
        if (!groupedMembers[groupKey]) {
          groupedMembers[groupKey] = {
            sortKey: item.group.sortKey,
            members: [],
          };
        }
        groupedMembers[groupKey].members.push(item);
      });

      // Sort groups and create member list with headers
      const members = [];
      Object.keys(groupedMembers)
        .sort((a, b) => groupedMembers[a].sortKey - groupedMembers[b].sortKey)
        .forEach((groupTitle) => {
          // Add group header
          members.push({
            groupHeader: groupTitle,
          });
          // Add sorted members
          groupedMembers[groupTitle].members
            .sort((a, b) =>
              a.user.displayName.localeCompare(b.user.displayName)
            )
            .forEach((item) => {
              members.push({
                user: item.user,
                summary: item.summary,
              });
            });
        });

      const totalMembers = this.performanceData.length;
      const percentage =
        totalMembers > 0
          ? Math.round((members.length / totalMembers) * 100)
          : 0;

      this.$emit("target-selected", {
        type: "memberList",
        chartContext: {
          label: "Erfüllungsrate je Zielvorgabe",
          icon: "mdi-chart-bar",
        },
        title: target.shortTitle || target.title,
        subtitle: status === "met" ? "Erfüllt" : "Nicht erfüllt",
        icon: status === "met" ? "mdi-check-circle" : "mdi-close-circle",
        iconColor: status === "met" ? "success" : "error",
        count: members.length,
        countLabel: "Mitglieder",
        targetValue: target.targetValue,
        targetUnit: target.unit,
        summary: [
          {
            label: "Anzahl",
            value: members.length,
            color: status === "met" ? "success" : "error",
          },
          {
            label: "Prozentsatz",
            value: `${percentage}%`,
            color: status === "met" ? "success" : "error",
          },
          {
            label: "Gesamt",
            value: totalMembers,
          },
        ],
        members: members,
      });
    },
  },
};
</script>
